import { PublishingStatuses } from '@opallabs/concepts'
import { getNestedProperty } from '@opallabs/lambda'
import { Content } from '@opallabs/wormhole/types'
import { User } from '../types'

const deliveryTypeMap = Object.freeze({
  all: 'Email and SMS',
  automatic: 'automatic delivery',
  email: 'Email',
  semi_automatic: 'one-click delivery',
  sms: 'SMS',
})

type DeliveryType = keyof typeof deliveryTypeMap

const providerMap = Object.freeze({
  spredfast: 'Khoros',
  social_studio: 'Social Studio',
  sprinklr_opal_io: 'Sprinklr',
})

type Provider = keyof typeof providerMap

type DeliveryRecord = {
  provider_name: Provider
  delivery_user_id: string
  delivery_user: { id: string }
  delivery_method: DeliveryType
}

const deliveryStrings = Object.freeze({
  noDelivery: 'Do not deliver',
  delivered: (recipient: string, type: string) =>
    `Has been delivered ${recipient} via ${type}`,
  error: (recipient: string) =>
    `An error occurred during delivery ${recipient}`,
  toBeDelivered: (recipient: string, type: string) =>
    `Set to deliver ${recipient} via ${type}`,
  published: 'Content is published',
})

const hasDeliveryType = (
  content: Partial<Pick<Content, 'publishing_mode' | 'delivery_record'>>
) => content.publishing_mode !== 'none'

const findDeliveryRecord = (
  content: Partial<Pick<Content, 'publishing_mode' | 'delivery_record'>>
): DeliveryRecord | undefined => {
  const record =
    getNestedProperty(content, 'delivery_record', 'attributes') ||
    getNestedProperty(content, 'delivery_record')
  return record
}

const hasBeenDelivered = (
  content: Partial<Pick<Content, 'delivery_record'>>
): string | undefined =>
  getNestedProperty(content, 'delivery_record', 'attributes', 'delivered_at')

const hasFailedDelivery = (
  content: Partial<Pick<Content, 'delivery_record'>>
): string | undefined =>
  getNestedProperty(content, 'delivery_record', 'attributes', 'failed_at')

const findPublishingMode = (
  content: Partial<Pick<Content, 'publishing_mode'>>
): string | undefined => getNestedProperty(content, 'publishing_mode')

const findIntegrationProvider = (
  content: Partial<Pick<Content, 'delivery_record'>>
): Provider | undefined => {
  const record = findDeliveryRecord(content)
  return record && record.provider_name
}

const findDeliveryUserId = (
  content: Partial<Pick<Content, 'delivery_record'>>
) => {
  const record = findDeliveryRecord(content)
  return (
    record &&
    (record.delivery_user_id ||
      (record.delivery_user && record.delivery_user.id))
  )
}

function findDeliveryMethod(
  content: Partial<Pick<Content, 'publishing_mode' | 'delivery_record'>>
): DeliveryType | undefined {
  const publishingMode = findPublishingMode(content)

  if (publishingMode === 'manual') {
    const record = findDeliveryRecord(content)
    return record && record.delivery_method
  }
  return publishingMode as DeliveryType
}

/**
 * Builds a clean, readable version of the delivery recipient.
 *   For manual delivery, this will be "to " + the actual user's name.
 *   For integration delivery, this will be "to " + the name of the service.
 *
 * Note that in the edge case where a user has defined a delivery
 *   method but no recipient, this will simply return an empty string.
 */
function parseDeliveryRecipient(
  content: Partial<Pick<Content, 'delivery_record' | 'publishing_mode'>>,
  users: Array<User>
) {
  const userId = findDeliveryUserId(content)
  const user = users.find((u) => Number(u.id) === Number(userId))
  if (user) {
    return `to ${user.fullName}`
  }

  const provider = findIntegrationProvider(content)
  if (provider) {
    return `to ${providerMap[provider]}`
  }

  return ''
}

/**
 * Builds a clean, readable version of the delivery method.
 *   For manual delivery, this will be something like "SMS" or "Email"
 *   For integration delivery, this will be "one-click delivery" or "automatic delivery"
 */
function parseDeliveryType(
  content: Partial<Pick<Content, 'publishing_mode' | 'delivery_record'>>
) {
  const method = findDeliveryMethod(content)
  const text = method ? deliveryTypeMap[method] || '' : ''
  return text
}

const contentIsPublished = (
  content: Partial<
    Pick<
      Content,
      'publishing_mode' | 'delivery_record' | 'post_state' | 'is_published'
    >
  >
) =>
  getNestedProperty(content ?? {}, 'post_state') === 'published' ||
  content.is_published

type DeliveryContent = {
  status: PublishingStatuses
  description: string
}

function getDeliveryContent(
  content: Partial<
    Pick<Content, 'publishing_mode' | 'delivery_record' | 'post_state'>
  >,
  users: Array<User>
): DeliveryContent {
  const attributedContent = content

  const recipientText = parseDeliveryRecipient(attributedContent, users)
  const type = parseDeliveryType(attributedContent)

  if (contentIsPublished(content)) {
    return {
      status: 'published',
      description: deliveryStrings.published,
    }
  }

  const publishingMode = attributedContent && attributedContent.publishing_mode
  if (!publishingMode || !hasDeliveryType(attributedContent) || !type) {
    return {
      status: 'delivery-not-sent',
      description: deliveryStrings.noDelivery,
    }
  }

  if (hasBeenDelivered(attributedContent)) {
    return {
      status: 'delivery-complete',
      description: deliveryStrings.delivered(recipientText, type),
    }
  }

  if (hasFailedDelivery(attributedContent)) {
    return {
      status: 'delivery-error',
      description: deliveryStrings.error(recipientText),
    }
  }

  return {
    status: 'delivery-not-sent',
    description: deliveryStrings.toBeDelivered(recipientText, type),
  }
}

export {
  deliveryTypeMap,
  findDeliveryMethod,
  findDeliveryRecord,
  findDeliveryUserId,
  findIntegrationProvider,
  findPublishingMode,
  hasBeenDelivered,
  hasDeliveryType,
  hasFailedDelivery,
  parseDeliveryRecipient,
  parseDeliveryType,
  providerMap,
  getDeliveryContent,
}
